import React from 'react';
import { Settings, Eye, Globe, Lock, Check, Users, AlertTriangle, Info } from 'lucide-react';
import { Input, Select } from './FormComponents';
import ApprovalSection from './ApprovalSection';
import './CourseCreationTabs.css';

export interface SettingsTabProps {
  // Visibility
  visibilityType: 'public' | 'restricted';
  onVisibilityTypeChange: (type: 'public' | 'restricted') => void;

  // Completion Rules
  requiredModules: boolean;
  minimumScore: string;
  sequentialLearning: boolean;
  managerApproval: boolean;
  onRequiredModulesChange: (value: boolean) => void;
  onMinimumScoreChange: (value: string) => void;
  onSequentialLearningChange: (value: boolean) => void;
  onManagerApprovalChange: (value: boolean) => void;

  // Enrollment
  enrollmentType: string;
  dueDate: string;
  sendReminders: boolean;
  onEnrollmentTypeChange: (value: string) => void;
  onDueDateChange: (value: string) => void;
  onSendRemindersChange: (value: boolean) => void;

  // Approval workflow
  requiresApproval: boolean;
  approverUserId: string;
  approverEmail: string;
  onRequiresApprovalChange: (value: boolean) => void;
  onApproverUserIdChange: (value: string) => void;
  onApproverEmailChange: (value: string) => void;

  validationErrors: Record<string, string>;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  visibilityType,
  onVisibilityTypeChange,
  requiredModules,
  minimumScore,
  sequentialLearning,
  managerApproval,
  onRequiredModulesChange,
  onMinimumScoreChange,
  onSequentialLearningChange,
  onManagerApprovalChange,
  enrollmentType,
  dueDate,
  sendReminders,
  onEnrollmentTypeChange,
  onDueDateChange,
  onSendRemindersChange,
  requiresApproval,
  approverUserId,
  approverEmail,
  onRequiresApprovalChange,
  onApproverUserIdChange,
  onApproverEmailChange,
  validationErrors,
}) => {
  return (
    <div className="tab-content">
      {/* Section Header */}
      <div className="section-header settings-header">
        <h3>
          <Settings size={20} />
          Course Settings
        </h3>
        <p>Configure visibility, completion rules, and enrollment options</p>
      </div>

      {/* Visibility & Access Control */}
      <div className="settings-section">
        <div className="section-title">
          <Eye size={18} />
          <h4>Visibility & Access Control</h4>
        </div>
        <p className="section-description">Control who can see and access this course</p>

        <div className="visibility-options">
          <button
            type="button"
            className={`visibility-option ${visibilityType === 'public' ? 'active' : ''}`}
            onClick={() => onVisibilityTypeChange('public')}
          >
            <div className="visibility-icon">
              <Globe size={24} />
            </div>
            <div className="visibility-content">
              <strong>Public / Organization-Wide</strong>
              <p>All users in your organization can see and access this course</p>
            </div>
          </button>

          <button
            type="button"
            className={`visibility-option ${visibilityType === 'restricted' ? 'active' : ''}`}
            onClick={() => onVisibilityTypeChange('restricted')}
          >
            <div className="visibility-icon">
              <Lock size={24} />
            </div>
            <div className="visibility-content">
              <strong>Restricted Access</strong>
              <p>Only specific groups, departments, or roles can access this course</p>
            </div>
          </button>
        </div>

        {visibilityType === 'public' && (
          <div className="info-box info-box-green">
            <span className="info-icon">
              <Globe size={16} />
            </span>
            <div>
              <p>This course will be visible and accessible to all users in your organization.</p>
            </div>
          </div>
        )}

        {visibilityType === 'restricted' && (
          <div className="info-box info-box-orange">
            <span className="info-icon">
              <AlertTriangle size={16} />
            </span>
            <div>
              <strong>Restricted access mode</strong>
              <p>Access restrictions can be configured after the course is created.</p>
            </div>
          </div>
        )}
      </div>

      {/* Completion Rules */}
      <div className="settings-section">
        <div className="section-title">
          <Check size={18} />
          <h4>Completion Rules</h4>
        </div>
        <p className="section-description">Define requirements for completing this course</p>

        <div className="toggle-list">
          <div className="toggle-row">
            <div>
              <strong>Required Modules</strong>
              <p>All modules must be completed</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={requiredModules}
                onChange={(e) => onRequiredModulesChange(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Minimum Score (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={minimumScore}
              onChange={(e) => onMinimumScoreChange(e.target.value)}
              placeholder="70"
            />
            <small className="form-help">Required passing score (0-100%)</small>
          </div>

          <div className="toggle-row">
            <div>
              <strong>Sequential Learning</strong>
              <p>Modules must be completed in order</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={sequentialLearning}
                onChange={(e) => onSequentialLearningChange(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="toggle-row">
            <div>
              <strong>Manager Approval</strong>
              <p>Requires permission from manager</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={managerApproval}
                onChange={(e) => onManagerApprovalChange(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Enrollment Settings */}
      <div className="settings-section">
        <div className="section-title">
          <Users size={18} />
          <h4>Enrollment Settings</h4>
        </div>
        <p className="section-description">Configure how users can enroll in this course</p>

        <div className="form-grid">
          <Select
            label="Enrollment Type"
            value={enrollmentType}
            onChange={(e) => onEnrollmentTypeChange(e.target.value)}
          >
            <option value="">Select</option>
            <option value="open">Open Enrollment</option>
            <option value="restricted">Restricted</option>
            <option value="auto">Auto-Enrollment</option>
          </Select>

          <div className="form-group">
            <label className="form-label">Due Date (Optional)</label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => onDueDateChange(e.target.value)}
            />
          </div>
        </div>

        <div className="toggle-row">
          <div>
            <strong>Email Reminders</strong>
            <p>Send automatic reminders to enrolled learners</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={sendReminders}
              onChange={(e) => onSendRemindersChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="info-box info-box-blue">
          <span className="info-icon">
            <Info size={16} />
          </span>
          <div>
            <strong>Post-Course Surveys</strong>
            <p>Survey settings can be configured after the course is created in the course management section.</p>
          </div>
        </div>
      </div>

      {/* Approval workflow */}
      <ApprovalSection
        requiresApproval={requiresApproval}
        approverUserId={approverUserId}
        approverEmail={approverEmail}
        onRequiresApprovalChange={onRequiresApprovalChange}
        onApproverUserIdChange={onApproverUserIdChange}
        onApproverEmailChange={onApproverEmailChange}
      />
    </div>
  );
};
